import { createFileRoute, Link, useRouter } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { organization } from '@/lib/auth-client'

export const Route = createFileRoute('/_authenticated/accept-invitation')({
  validateSearch: (search: Record<string, unknown>): { id?: string } =>
    typeof search.id === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(search.id)
      ? { id: search.id }
      : {},
  component: AcceptInvitationPage,
})

function AcceptInvitationPage() {
  const { id } = Route.useSearch()
  const router = useRouter()
  const [pending, setPending] = useState(false)

  async function accept() {
    if (!id) return
    setPending(true)
    const { error: acceptError } = await organization.acceptInvitation({
      invitationId: id,
    })
    setPending(false)
    if (acceptError) {
      toast.error('Could not accept invitation', {
        description: acceptError.message,
      })
      return
    }
    router.invalidate()
    window.location.href = '/organization'
  }

  async function reject() {
    if (!id) return
    setPending(true)
    await organization.rejectInvitation({ invitationId: id })
    setPending(false)
    window.location.href = '/dashboard'
  }

  return (
    <main className="mx-auto flex min-h-[70vh] w-full max-w-sm flex-col justify-center px-4 py-16">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Organization invitation</CardTitle>
          <CardDescription>
            {id
              ? 'You were invited to join an organization.'
              : 'This invitation link is incomplete. Ask the sender for a new one.'}
          </CardDescription>
        </CardHeader>
        {id ? (
          <>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Accept to join, or decline to dismiss.
              </p>
            </CardContent>
            <CardFooter className="gap-3">
              <Button onClick={accept} disabled={pending}>
                {pending ? 'Working…' : 'Accept'}
              </Button>
              <Button variant="outline" onClick={reject} disabled={pending}>
                Decline
              </Button>
            </CardFooter>
          </>
        ) : (
          <CardFooter>
            <Button asChild variant="outline">
              <Link to="/dashboard">Back to dashboard</Link>
            </Button>
          </CardFooter>
        )}
      </Card>
    </main>
  )
}
